import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import { Section } from './Section';
import { useCopy } from '@/i18n';
import { useStorePacks, type StorePack } from '@/marketing/queries/useStorePacks';
import { useAuthStore } from '@/platform/stores/useAuthStore';

type StoreCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  buy: string;
  includes: string;
  offline_note: string;
  offline_unavailable: string;
  error: string;
};

// Glyph tiles in place of ecommerce-6's product photos.
const PACK_GLYPHS: Record<string, string> = {
  'pack-medical': '💊',
  'pack-admin': '🗂️',
  'pack-housing': '🏠',
};
const TILE_BGS = ['var(--sand)', 'var(--paper-2)', 'var(--navy)'];

// Pack storefront adapted from reactbits-pro ecommerce-6: catalog grid with
// tile, name, price, and an animated entrance. Buy → Stripe checkout via the
// existing productKey flow (unauthenticated visitors go to signup first).
export function PackStoreSection() {
  const copy = useCopy();
  const c = copy<StoreCopy>('store');
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const { data: packs, error, isLoading } = useStorePacks();
  const isAuthed = useAuthStore((s) => Boolean(s.user));
  const navigate = useNavigate();
  const [offlineNotice, setOfflineNotice] = useState(false);
  const [buyingKey, setBuyingKey] = useState<string | null>(null);

  if (!c) return null;
  // The store hides itself until sellable packs exist (or while erroring).
  if (error || (!isLoading && (packs?.length ?? 0) === 0)) return null;

  const priceFmt = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' });

  async function buy(pack: StorePack) {
    if (!navigator.onLine) {
      setOfflineNotice(true);
      return;
    }
    if (!isAuthed) {
      void navigate(`/signup?next=${encodeURIComponent('/store')}`);
      return;
    }
    setBuyingKey(pack.productKey);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          productKey: pack.productKey,
          successUrl: `${window.location.origin}/downloads?purchase=success`,
          cancelUrl: `${window.location.origin}/store`,
        }),
      });
      if (res.ok) {
        const { url } = (await res.json()) as { url: string };
        window.location.assign(url);
        return;
      }
    } catch {
      // fall through to reset
    }
    setBuyingKey(null);
  }

  return (
    <Section id="store" bg="paper">
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <span className="eyebrow">{c.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {c.title}
        </h2>
        <p className="lead" style={{ maxWidth: '52ch', margin: '18px auto 0' }}>
          {c.lead}
        </p>
        <p style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>{c.offline_note}</p>
        {offlineNotice ? (
          <p
            role="status"
            className="mx-auto mt-4 flex w-fit items-center gap-2 rounded-full px-4 py-2"
            style={{
              fontSize: 13,
              color: 'var(--ink)',
              background: 'var(--paper-2)',
              border: '1px solid var(--line)',
            }}
          >
            <WifiOff className="h-4 w-4" style={{ color: 'var(--coral)' }} />
            {c.offline_unavailable}
          </p>
        ) : null}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? [0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-72 animate-pulse rounded-2xl"
                style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
                aria-hidden
              />
            ))
          : (packs ?? []).map((pack, i) => {
              const count = pack.metadata?.scenarioCount;
              return (
                <motion.article
                  key={pack.id}
                  initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.35,
                    delay: reduceMotion ? 0 : (i % 3) * 0.05,
                  }}
                  className="group flex flex-col overflow-hidden rounded-2xl"
                  style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
                >
                  <div
                    className="grid aspect-[2/1] place-items-center overflow-hidden"
                    style={{ background: TILE_BGS[i % TILE_BGS.length] }}
                  >
                    <span
                      className="transition-transform duration-500 group-hover:scale-110"
                      style={{ fontSize: 64 }}
                      aria-hidden
                    >
                      {PACK_GLYPHS[pack.slug] ?? '🎒'}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 22,
                          color: 'var(--ink)',
                        }}
                      >
                        {pack.title}
                      </h3>
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 20,
                          color: 'var(--coral)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {priceFmt.format(pack.priceCents / 100)}
                      </span>
                    </div>
                    {typeof count === 'number' ? (
                      <p
                        style={{
                          marginTop: 4,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          letterSpacing: '.1em',
                          textTransform: 'uppercase',
                          color: 'var(--muted)',
                        }}
                      >
                        {t('store.includes', { count })}
                      </p>
                    ) : null}
                    {pack.description ? (
                      <p
                        style={{
                          marginTop: 10,
                          fontSize: 14,
                          lineHeight: 1.6,
                          color: 'var(--muted)',
                          flex: 1,
                        }}
                      >
                        {pack.description}
                      </p>
                    ) : (
                      <span style={{ flex: 1 }} />
                    )}
                    <button
                      type="button"
                      onClick={() => void buy(pack)}
                      disabled={buyingKey === pack.productKey}
                      className="btn btn-primary mt-5"
                      style={{ justifyContent: 'center', width: '100%' }}
                    >
                      {c.buy}
                    </button>
                  </div>
                </motion.article>
              );
            })}
      </div>
    </Section>
  );
}
